"use client";

import { useState, useEffect, useRef, useCallback, ReactElement } from "react";
import { Turnstile } from "@/components/auth/turnstile";

interface UseTurnstileTokenOptions {
  /**
   * Whether to auto-refresh token when it's about to expire
   * Default: true
   */
  autoRefresh?: boolean;
  /**
   * Token age threshold in seconds before auto-refresh
   * Default: 75 (refresh if token is older than 75 seconds)
   */
  refreshThresholdSeconds?: number;
  /**
   * Whether to skip Turnstile in development
   * @deprecated No longer used - Turnstile always runs (even in dev) for login/signup/add-phone flows
   * Turnstile must allow localhost/127.0.0.1 in Cloudflare Turnstile widget settings for local dev
   */
  skipInDevelopment?: boolean;
  /**
   * Whether to render Turnstile visibly (not hidden)
   * Default: false (hidden)
   */
  visible?: boolean;
}

interface UseTurnstileTokenReturn {
  /**
   * Current Turnstile token (null if not yet obtained or expired)
   */
  token: string | null;
  /**
   * Timestamp when token was obtained (milliseconds since epoch)
   */
  tokenTimestamp: number | null;
  /**
   * Whether Turnstile is currently verifying
   */
  isVerifying: boolean;
  /**
   * Simple boolean: true if token exists (verification complete)
   * CRITICAL: This is based ONLY on token existence, not iframe state
   */
  isVerified: boolean;
  /**
   * Error message if verification failed
   */
  error: string | null;
  /**
   * Manually refresh the token
   */
  refreshToken: () => Promise<string | null>;
  /**
   * Get a valid token, refreshing if necessary
   * Returns null if unable to get a token
   */
  getValidToken: () => Promise<string | null>;
  /**
   * Get a fresh token (forces refresh if token is older than 60 seconds)
   * This ensures every API call gets a CURRENT token
   */
  getFreshToken: () => Promise<string | null>;
  /**
   * Get token for submit WITHOUT refreshing or re-rendering the widget.
   * Returns current token if it exists and is not expired (age < 5 min), else null.
   * Use this at submit time to avoid remounting the Turnstile iframe during submit.
   */
  getTokenForSubmit: () => Promise<string | null>;
  /**
   * Whether Turnstile is enabled for this environment (production + site key, not emulator).
   */
  isTurnstileEnabled: boolean;
  /**
   * True while Cloudflare is showing the "Verify you are human" checkbox and
   * waiting to be clicked. Having no token in that state is normal, so callers
   * that bound the wait must not count this time against it.
   */
  isAwaitingInteraction: boolean;
  /**
   * Turnstile component to render (visible or hidden based on options)
   */
  TurnstileComponent: () => ReactElement | null;
  /**
   * Reset the Turnstile widget (useful when API returns Turnstile failure)
   */
  resetWidget: () => void;
}

/**
 * Shared hook for managing Turnstile token
 * 
 * Features:
 * - Renders Turnstile invisibly on page load
 * - Automatically obtains token in background
 * - Auto-refreshes token if older than threshold
 * - Provides methods to get/refresh token before submit
 * 
 * CRITICAL: Uses real Turnstile tokens in production, dev-bypass in development
 * - In development: Uses dev-bypass-token (Cloudflare doesn't support localhost)
 * - In production: Requires real Turnstile token
 * 
 * Usage:
 * ```tsx
 * const { token, getValidToken, TurnstileComponent } = useTurnstileToken();
 * 
 * // Render the component (hidden)
 * <TurnstileComponent />
 * 
 * // On form submit:
 * const validToken = await getValidToken();
 * ```
 */
export function useTurnstileToken(
  options: UseTurnstileTokenOptions = {}
): UseTurnstileTokenReturn {
  const {
    autoRefresh = true,
    refreshThresholdSeconds = 75,
    skipInDevelopment = true,
    visible = false,
  } = options;

  const [token, setToken] = useState<string | null>(null);
  const [tokenTimestamp, setTokenTimestamp] = useState<number | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0); // Key for forcing widget remount
  /* True while Cloudflare is showing the "Verify you are human" checkbox. The
     absence of a token then means "waiting for the person", not "broken", and
     callers must not time out against it. */
  const [isAwaitingInteraction, setIsAwaitingInteraction] = useState(false);

  const turnstileExecuteRef = useRef<{ execute: () => void; reset: () => void } | null>(null);
  const isInitializedRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const isProduction = process.env.NODE_ENV === "production";
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
  
  // Detect if running on Android emulator
  const isAndroidEmulator = typeof window !== "undefined" && 
    /Android/i.test(navigator.userAgent) && 
    (
      /Emulator|emulator|sdk|SDK|Genymotion|BlueStacks/i.test(navigator.userAgent) ||
      /Android.*x86|Android.*x86_64/i.test(navigator.userAgent) ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) ||
      (screen.width <= 480 && screen.height <= 800)
    );

  // Android Capacitor (WebView): Turnstile is unreliable; do not load widget or require token
  const isAndroidCapacitor = typeof window !== "undefined" &&
    /Android/i.test(navigator.userAgent) &&
    /Capacitor/i.test(navigator.userAgent);

  // iOS Capacitor (WKWebView): same problem, and it locked people out of the app.
  //
  // Android has been skipping Turnstile since the comment above was written, on the grounds that it
  // is unreliable in a WebView. iOS was never given the same treatment, so iPhone users ran the full
  // challenge inside WKWebView. On 2026-07-29 that left the sign-in button stuck on "Completing
  // verification…" with no token and no error callback, and no way into the app at all.
  //
  // Detection cannot use the User-Agent the way Android does: capacitor.config.ts only appends
  // " Capacitor" to the UA on Android, so on iOS there is nothing to match. Capacitor.getPlatform()
  // is the reliable signal, and the matching `x-platform` header (see lib/api/auth-fetch.ts) is what
  // the server checks.
  //
  // This is the same trust level as the existing Android path, not a weaker one: a UA is just as
  // forgeable as a header, and the auth routes are rate limited either way.
  const isIosCapacitor = typeof window !== "undefined" &&
    (() => {
      try {
        const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
        return cap?.getPlatform?.() === "ios";
      } catch {
        return false;
      }
    })();

  const isNativeCapacitor = isAndroidCapacitor || isIosCapacitor;

  // CRITICAL: Use real Turnstile in production, but allow dev-bypass in development
  // Skip on Android emulators and on native Capacitor shells (Turnstile unreliable in WebView)
  const shouldUseTurnstile = isProduction && !isAndroidEmulator && !isNativeCapacitor && !!turnstileSiteKey;

  // Check if token is expired or about to expire
  const isTokenExpired = useCallback((): boolean => {
    if (!token || !tokenTimestamp) return true;
    const ageSeconds = (Date.now() - tokenTimestamp) / 1000;
    return ageSeconds >= refreshThresholdSeconds;
  }, [token, tokenTimestamp, refreshThresholdSeconds]);

  // Track page load time for timing logs
  const pageLoadTimeRef = useRef<number>(Date.now());

  // Handle successful verification
  const handleVerify = useCallback((newToken: string) => {
    const tokenReadyTime = Date.now();
    const timeToToken = tokenReadyTime - pageLoadTimeRef.current;
    
    // CRITICAL: Set token and timestamp FIRST, then clear verifying state
    // This ensures token is available immediately when Turnstile completes
    setToken(newToken);
    setTokenTimestamp(Date.now());
    // Clear verifying state AFTER token is set to prevent race conditions
    setIsVerifying(false);
    setError(null);
  }, []);

  // Handle verification error
  const handleError = useCallback((errorCode: string) => {
    console.error("[useTurnstileToken] Verification error:", errorCode);
    setToken(null);
    setTokenTimestamp(null);
    setIsVerifying(false);
    setError("Verification failed. Please try again.");
  }, []);

  // Handle token expiration
  const handleExpire = useCallback(() => {
    setToken(null);
    setTokenTimestamp(null);
    setIsVerifying(false);
    
    // Auto-reset widget
    if (visible && shouldUseTurnstile) {
      // For visible mode, force remount to trigger new verification
      setTimeout(() => {
        setWidgetKey(prev => prev + 1);
      }, 100);
    } else if (turnstileExecuteRef.current) {
      // For hidden/manual mode, use executeRef reset
      setTimeout(() => {
        turnstileExecuteRef.current?.reset();
      }, 100);
    }
  }, [visible, shouldUseTurnstile]);

  // Store pending promise resolvers for token callbacks
  const pendingResolversRef = useRef<Map<number, { resolve: (token: string | null) => void; timeout: NodeJS.Timeout }>>(new Map());
  const resolverIdRef = useRef(0);

  // Enhanced handleVerify that resolves pending promises
  const handleVerifyWithResolve = useCallback((newToken: string) => {
    // CRITICAL: Set token state FIRST before resolving promises
    // This ensures token is available immediately
    const tokenReadyTime = Date.now();
    setToken(newToken);
    setTokenTimestamp(tokenReadyTime);
    setIsVerifying(false);
    setError(null);
    
    // Resolve all pending promises with the token
    pendingResolversRef.current.forEach(({ resolve, timeout }) => {
      clearTimeout(timeout);
      resolve(newToken);
    });
    pendingResolversRef.current.clear();
    
    // Also call handleVerify for consistency (though state is already set)
    handleVerify(newToken);
  }, [handleVerify]);

  // Enhanced handleError that rejects pending promises
  const handleErrorWithResolve = useCallback((errorCode: string) => {
    handleError(errorCode);
    // Reject all pending promises
    pendingResolversRef.current.forEach(({ resolve, timeout }) => {
      clearTimeout(timeout);
      resolve(null);
    });
    pendingResolversRef.current.clear();
  }, [handleError]);

  // Execute Turnstile to get a new token
  const executeTurnstile = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!shouldUseTurnstile) {
        // In development or on emulator, return bypass token
        // Cloudflare Turnstile doesn't support localhost, so we bypass in dev
        if (!isProduction) {
          const bypassToken = "dev-bypass-token";
          setToken(bypassToken);
          setTokenTimestamp(Date.now());
          resolve(bypassToken);
          return;
        }
        // On Android emulators in production, Turnstile may not work
        console.error("[useTurnstileToken] Turnstile not available (emulator detected)");
        setError("Verification not available on this device.");
        resolve(null);
        return;
      }

      if (!turnstileExecuteRef.current) {
        console.error("[useTurnstileToken] Turnstile execute ref not available");
        setError("Verification not ready. Please refresh the page.");
        resolve(null);
        return;
      }

      setIsVerifying(true);
      setError(null);

      // Generate unique ID for this resolver
      const resolverId = resolverIdRef.current++;
      
      // Set up timeout in case verification takes too long
      const timeout = setTimeout(() => {
        pendingResolversRef.current.delete(resolverId);
        setIsVerifying(false);
        setError("Verification timed out. Please try again.");
        resolve(null);
      }, 30000); // 30 second timeout

      // Store resolver with timeout
      pendingResolversRef.current.set(resolverId, { resolve, timeout });
      
      try {
        turnstileExecuteRef.current.execute();
      } catch (err) {
        pendingResolversRef.current.delete(resolverId);
        clearTimeout(timeout);
        console.error("[useTurnstileToken] Error executing Turnstile:", err);
        setIsVerifying(false);
        setError("Verification failed. Please try again.");
        resolve(null);
      }
    });
  }, [shouldUseTurnstile]);

  // Initialize Turnstile on mount
  useEffect(() => {
    pageLoadTimeRef.current = Date.now();

    if (isInitializedRef.current) return;
    
    // CRITICAL: In development, always set bypass token immediately (regardless of visible mode)
    // Cloudflare doesn't support localhost, so we bypass in dev
    if (!isProduction) {
      setToken("dev-bypass-token");
      setTokenTimestamp(Date.now());
      isInitializedRef.current = true;
      return;
    }

    // In production, check if Turnstile should be used
    if (!shouldUseTurnstile) {
      // On Android emulators in production, Turnstile may not work
      console.warn("[useTurnstileToken] Turnstile not available (emulator detected)");
      isInitializedRef.current = true;
      return;
    }

    // For visible mode in production, we don't use manual execution - widget auto-runs
    if (visible) {
      isInitializedRef.current = true;
      return;
    }

    // For hidden mode, wait for Turnstile to be ready (check periodically)
    const checkAndInit = () => {
      if (turnstileExecuteRef.current && !isInitializedRef.current) {
        isInitializedRef.current = true;
        executeTurnstile().catch((err) => {
          console.error("[useTurnstileToken] Initial token fetch failed:", err);
        });
      } else if (!isInitializedRef.current) {
        // Check again after a short delay
        setTimeout(checkAndInit, 100);
      }
    };

    // Start checking after a short initial delay
    const initTimeout = setTimeout(checkAndInit, 300);

    return () => {
      clearTimeout(initTimeout);
    };
  }, [shouldUseTurnstile, executeTurnstile, visible]);

  // Auto-refresh token if it's about to expire
  useEffect(() => {
    if (!autoRefresh || !shouldUseTurnstile || !token) return;

    const checkAndRefresh = () => {
      if (isTokenExpired()) {
        executeTurnstile().catch((err) => {
          console.error("[useTurnstileToken] Auto-refresh failed:", err);
        });
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkAndRefresh, 10000);
    
    return () => {
      clearInterval(interval);
    };
  }, [autoRefresh, shouldUseTurnstile, token, isTokenExpired, executeTurnstile]);

  // Manual refresh function
  const refreshToken = useCallback(async (): Promise<string | null> => {
    setToken(null);
    setTokenTimestamp(null);
    
    // In visible mode, reset widget and wait for it to auto-complete
    if (visible) {
      setIsVerifying(true);
      setError(null);
      
      // Reset widget by remounting it
      setWidgetKey(prev => prev + 1);
      
      // Wait for verification to complete (up to 30 seconds)
      return new Promise((resolve) => {
        const startTime = Date.now();
        const maxWait = 30000; // 30 seconds
        
        const resolverId = resolverIdRef.current++;
        const timeout = setTimeout(() => {
          pendingResolversRef.current.delete(resolverId);
          setIsVerifying(false);
          setError("Verification timed out. Please try again.");
          resolve(null);
        }, maxWait);
        
        // Store resolver - will be called when handleVerifyWithResolve is triggered
        pendingResolversRef.current.set(resolverId, { 
          resolve: (newToken) => {
            clearTimeout(timeout);
            pendingResolversRef.current.delete(resolverId);
            resolve(newToken);
          }, 
          timeout 
        });
      });
    }
    
    // Hidden mode: use executeRef
    if (turnstileExecuteRef.current) {
      turnstileExecuteRef.current.reset();
    }
    
    // Get new token
    return executeTurnstile();
  }, [executeTurnstile, visible]);

  // Get valid token - use existing token if available and fresh, otherwise get a new one
  // CRITICAL: For visible mode, we should use the token that was just verified by the widget
  // Only refresh if the token is expired, missing, or too old
  // CRITICAL FIX: Trust the token if it exists and is valid, even if isVerifying is true
  // This fixes the issue where Turnstile shows success but app still thinks it's verifying
  const getValidToken = useCallback(async (): Promise<string | null> => {
    // NATIVE APP FIX: when Turnstile is skipped (Android or iOS Capacitor, or an Android emulator),
    // return a placeholder so callers do not block. The server independently re-checks that the
    // request really is a native shell via isNativeCapacitorRequest() before honouring it; the
    // placeholder alone grants nothing.
    //
    // The string stays "android-capacitor-skip" deliberately. It is a wire value that older app
    // builds already send, and those builds are in the App Store and Play Store and cannot be
    // updated remotely. Renaming it would reject every existing native install.
    if (!shouldUseTurnstile) {
      return isProduction ? "android-capacitor-skip" : "dev-bypass-token";
    }

    // CRITICAL FIX: If we have a valid token, use it immediately
    // Don't wait for isVerifying to clear - if token exists, Turnstile has completed
    if (token && tokenTimestamp) {
      const ageSeconds = (Date.now() - tokenTimestamp) / 1000;
      const maxValidAgeSeconds = 300; // 5 minutes - Turnstile tokens are valid for 5 minutes
      
      if (ageSeconds < maxValidAgeSeconds) {
        // Token is valid and not expired - use it immediately
        // Clear verifying state if it's still true (state update might be pending)
        if (isVerifying) {
          setIsVerifying(false);
        }
        return token;
      }
      // else: token is expired - fall through to get a new one
    }

    // In visible mode, if widget is currently verifying and we don't have a token yet, wait for it
    if (visible && isVerifying && !token) {
      // Wait for verification to complete (up to 30 seconds)
      return new Promise((resolve) => {
        const startTime = Date.now();
        const maxWait = 30000; // 30 seconds
        
        const resolverId = resolverIdRef.current++;
        const timeout = setTimeout(() => {
          pendingResolversRef.current.delete(resolverId);
          console.warn("[useTurnstileToken] Timeout waiting for visible widget verification");
          resolve(null);
        }, maxWait);
        
        // Store resolver - will be called when handleVerifyWithResolve is triggered
        pendingResolversRef.current.set(resolverId, { 
          resolve: (newToken) => {
            clearTimeout(timeout);
            pendingResolversRef.current.delete(resolverId);
            resolve(newToken);
          }, 
          timeout 
        });
      });
    }

    // Token is missing or expired - get a fresh one
    return refreshToken();
  }, [token, tokenTimestamp, refreshToken, visible, isVerifying]);

  // Method to reset widget (useful when API returns Turnstile failure)
  // Declared before getFreshToken so it can be used in its dependency array
  const resetWidget = useCallback(() => {
    setToken(null);
    setTokenTimestamp(null);
    setIsVerifying(false);
    setError(null);
    
    if (visible) {
      setWidgetKey(prev => prev + 1);
    } else if (turnstileExecuteRef.current) {
      // For hidden/manual mode, use executeRef reset
      turnstileExecuteRef.current.reset();
    }
  }, [visible]);

  // Get token for submit WITHOUT refreshing or re-rendering the widget.
  // Returns current token if it exists and age < 5 min (Turnstile validity), else null.
  const getTokenForSubmit = useCallback(async (): Promise<string | null> => {
    const maxValidAgeSeconds = 300; // 5 minutes - Turnstile tokens are valid for 5 minutes
    if (token && tokenTimestamp) {
      const ageSeconds = (Date.now() - tokenTimestamp) / 1000;
      if (ageSeconds < maxValidAgeSeconds) {
        return token;
      }
    }
    return null;
  }, [token, tokenTimestamp]);

  // Get fresh token (forces refresh if token is older than 60 seconds)
  // This ensures every API call gets a CURRENT token, not a stale one
  // For visible mode, prefers using existing token if it's fresh
  const getFreshToken = useCallback(async (): Promise<string | null> => {
    const maxFreshAgeSeconds = 60; // 60 seconds - tokens older than this are considered stale
    
    // If we have a token, check if it's fresh enough
    if (token && tokenTimestamp) {
      const ageSeconds = (Date.now() - tokenTimestamp) / 1000;
      if (ageSeconds < maxFreshAgeSeconds) {
        return token;
      }
      
      // Token is stale - force refresh
      // Only reset widget if not in visible mode (visible widget should auto-refresh)
      if (!visible) {
        resetWidget();
      }
      return refreshToken();
    }
    
    // If currently verifying, wait for it to complete
    if (isVerifying) {
      return new Promise((resolve) => {
        const startTime = Date.now();
        const maxWait = 30000; // 30 seconds
        
        const checkInterval = setInterval(() => {
          // Use closure to get latest token state
          const currentToken = token;
          const currentTimestamp = tokenTimestamp;
          const currentIsVerifying = isVerifying;
          
          if (!currentIsVerifying && currentToken && currentTimestamp) {
            clearInterval(checkInterval);
            const ageSeconds = (Date.now() - currentTimestamp) / 1000;
            if (ageSeconds < maxFreshAgeSeconds) {
              resolve(currentToken);
            } else {
              // Token obtained but is stale - refresh
              if (!visible) {
                resetWidget();
              }
              refreshToken().then(resolve);
            }
          } else if (Date.now() - startTime > maxWait) {
            clearInterval(checkInterval);
            console.error("[useTurnstileToken] Timeout waiting for verification");
            resolve(null);
          }
        }, 100);
      });
    }
    
    // Not verifying - get a new token
    return refreshToken();
  }, [token, tokenTimestamp, isVerifying, refreshToken, resetWidget, visible]);

  // Turnstile component (visible or hidden based on options)
  const TurnstileComponent = useCallback(() => {
    // CRITICAL: In dev mode, still render the component (it will auto-verify with dev-bypass-token)
    // This ensures the component's useEffect can also set the token as a backup
    // In production, only render if shouldUseTurnstile is true
    if (isProduction && !shouldUseTurnstile) {
      return null;
    }

    // Visible mode: render normally, auto-runs on page load
    // In dev mode, component will auto-verify with dev-bypass-token via its useEffect
    if (visible) {
      return (
        <div key={widgetKey} className="w-full block py-2" style={{ display: 'block' }}>
          <Turnstile
            siteKey={turnstileSiteKey}
            onVerify={handleVerifyWithResolve}
            onError={handleErrorWithResolve}
            onExpire={handleExpire}
            onInteractiveChange={setIsAwaitingInteraction}
            theme="light"
            size="normal"
            manual={false}
          />
        </div>
      );
    }

    // Hidden mode: render invisibly, uses manual execution
    return (
      <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
        <Turnstile
          siteKey={turnstileSiteKey}
          onVerify={handleVerifyWithResolve}
          onError={handleErrorWithResolve}
          onExpire={handleExpire}
          onInteractiveChange={setIsAwaitingInteraction}
          theme="light"
          size="normal"
          manual={true}
          executeRef={turnstileExecuteRef}
        />
      </div>
    );
  }, [isProduction, shouldUseTurnstile, turnstileSiteKey, handleVerifyWithResolve, handleErrorWithResolve, handleExpire, visible, widgetKey]);

  // CRITICAL: isVerified is based ONLY on token existence, not iframe state
  // This ensures we never rely on cross-origin iframe access
  const isVerified = !!token;

  return {
    token,
    tokenTimestamp,
    isVerifying,
    isVerified,
    error,
    refreshToken,
    getValidToken,
    getFreshToken,
    getTokenForSubmit,
    isTurnstileEnabled: shouldUseTurnstile,
    isAwaitingInteraction,
    TurnstileComponent,
    resetWidget,
  };
}
